<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { agruparParaSectores, colorDeSlot, esModoOscuro } from '../lib/palette.js'
import { repartirAngulos } from '../lib/geometry.js'
import { num, pct } from '../lib/format.js'

const props = defineProps({
  /** [{ id, name, value }] sin agrupar: el componente hace el top N + Otros. */
  items: { type: Array, required: true },
  total: { type: Number, default: 0 },
  etiquetaTotal: { type: String, default: 'leads' },
  size: { type: Number, default: 260 },
})
const emit = defineEmits(['seleccionar'])

const oscuro = ref(false)
let mq

onMounted(() => {
  oscuro.value = esModoOscuro()
  mq = window.matchMedia?.('(prefers-color-scheme: dark)')
  mq?.addEventListener('change', sincronizar)
  observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
})
onBeforeUnmount(() => {
  mq?.removeEventListener('change', sincronizar)
  observador.disconnect()
})
const sincronizar = () => { oscuro.value = esModoOscuro() }
const observador = new MutationObserver(sincronizar)

const activo = ref(null)

const suma = computed(() => props.items.reduce((s, i) => s + i.value, 0))

const sectores = computed(() =>
  repartirAngulos(agruparParaSectores(props.items)).map((g) => ({
    ...g,
    color: colorDeSlot(g.slot, oscuro.value),
  })),
)

const destacado = computed(() => sectores.value.find((s) => s.id === activo.value) || null)
</script>

<template>
  <div class="donut">
    <div class="grafico" :style="{ width: size + 'px' }">
      <svg :viewBox="`-110 -110 220 220`" role="img" :aria-label="`Reparto de ${etiquetaTotal} por proyecto`">
        <path
          v-for="s in sectores" :key="s.id"
          :d="s.d" :fill="s.color"
          :class="{ apagado: activo && activo !== s.id }"
          @mouseenter="activo = s.id" @mouseleave="activo = null"
          @click="emit('seleccionar', s)"
        >
          <title>{{ s.name }}: {{ num(s.value) }} ({{ pct(s.fraccion) }})</title>
        </path>
      </svg>

      <div class="centro">
        <template v-if="destacado">
          <strong>{{ num(destacado.value) }}</strong>
          <span>{{ pct(destacado.fraccion) }}</span>
          <em>{{ destacado.name }}</em>
        </template>
        <template v-else>
          <strong>{{ num(total || suma) }}</strong>
          <span>{{ etiquetaTotal }}</span>
        </template>
      </div>
    </div>

    <!-- Leyenda con el valor escrito: la identidad nunca depende solo del color. -->
    <ul class="leyenda">
      <li
        v-for="s in sectores" :key="s.id"
        :class="{ apagado: activo && activo !== s.id }"
        @mouseenter="activo = s.id" @mouseleave="activo = null"
        @click="emit('seleccionar', s)"
      >
        <span class="punto" :style="{ background: s.color }" />
        <span class="nombre" :title="s.agrupa ? `${s.agrupa} proyectos mas` : s.name">
          {{ s.name }}
          <em v-if="s.agrupa" class="muted">({{ s.agrupa }} proyectos)</em>
        </span>
        <span class="mono cifra">{{ num(s.value) }}</span>
        <span class="mono porc muted">{{ pct(s.fraccion) }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.donut { display: flex; gap: 26px; align-items: center; flex-wrap: wrap; }
.grafico { position: relative; flex: none; }
svg { width: 100%; height: auto; display: block; }
path { cursor: pointer; transition: opacity .12s ease; }
path.apagado { opacity: .28; }

.centro {
  position: absolute; inset: 0; display: grid; align-content: center;
  justify-items: center; pointer-events: none; text-align: center; padding: 0 22%;
}
.centro strong { font-size: 26px; letter-spacing: -.02em; line-height: 1.1; }
.centro span { font-size: 11.5px; color: var(--muted); }
.centro em {
  font-style: normal; font-size: 11px; color: var(--muted); margin-top: 2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;
}

.leyenda { list-style: none; margin: 0; padding: 0; flex: 1; min-width: 240px; display: grid; gap: 3px; }
.leyenda li {
  display: grid; grid-template-columns: 10px 1fr auto 48px; gap: 9px;
  align-items: center; font-size: 12.5px; padding: 3px 6px; border-radius: 6px;
  cursor: pointer; transition: background .12s ease, opacity .12s ease;
}
.leyenda li:hover { background: var(--surface-2); }
.leyenda li.apagado { opacity: .45; }
.punto { width: 10px; height: 10px; border-radius: 3px; }
.nombre { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nombre em { font-size: 11px; }
.cifra { font-weight: 600; }
.porc { text-align: right; }
</style>
